// src/components/CalendarPage.js
import { useState, useEffect } from "react";
import { useContacts } from "../context/ContactsContext";
import { useEvents } from "../context/EventsContext";
import ContactInfo from "./ContactInfo";
import ContactSearchBar from "./ContactSearchBar";
import "./CalendarPage.css";

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function generateCalendarDays(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startOffset = (firstDay.getDay() + 6) % 7;
  const days = [];

  // Vormonat auffüllen
  const prevMonthLastDate = new Date(year, month, 0).getDate();
  for (let i = 0; i < startOffset; i++) {
    days.push(
      new Date(year, month - 1, prevMonthLastDate - (startOffset - 1 - i))
    );
  }

  // aktueller Monat
  for (let d = 1; d <= daysInMonth; d++) {
    days.push(new Date(year, month, d));
  }

  // Folgemonat auffüllen bis 42 Felder (6 Wochen Grid)
  const totalCells = 42;
  const trailingCount = totalCells - days.length;
  for (let i = 1; i <= trailingCount; i++) {
    days.push(new Date(year, month + 1, i));
  }

  return days;
}

export default function CalendarPage() {
  const { selectedContactId, setSelectedContactId, contacts } = useContacts(); // ✅ contacts hinzugefügt
  const { events, loadEvents } = useEvents();
  const [setSearchTerm] = useState("");

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(new Date());
  const [showPastEvents, setShowPastEvents] = useState(false);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const calendarDays = generateCalendarDays(year, month);

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  // Events laden wenn Monat ändert (inkl. Vormonat und Folgemonat für Kalenderanzeige)
  useEffect(() => {
    const from = new Date(year, month - 1, 1).toISOString().split("T")[0];
    const to = new Date(year, month + 2, 0).toISOString().split("T")[0];
    loadEvents(from, to);
  }, [year, month, loadEvents]);

  function formatLocalDate(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  function eventsForDate(date) {
    const dateStr = formatLocalDate(date);
    return events.filter((e) => e.day === dateStr);
  }

  // Events für das ausgewählte Datum
  const selectedDayEvents = eventsForDate(selectedCalendarDate);
  
  // Alle Events vor dem heutigen Datum
  const todayDate = new Date();
  const todayStr = formatLocalDate(todayDate);
  const pastEvents = events.filter((e) => e.day < todayStr);

  const eventsToDisplay = showPastEvents 
    ? pastEvents
    : selectedDayEvents;

  return (
    <div className="calendar-page">
      <div
        className={`calendar-layout ${
          selectedContactId ? "with-contact" : "no-contact"
        }`}
      >
        {/* Linke Spalte: SearchBar + Kalender */}
        <div className="calendar-left-panel" style={{ position: "relative" }}>
          <ContactSearchBar 
            onSearch={setSearchTerm} 
            onSelectSuggestion={(contact) => setSelectedContactId(contact.id)}
          />

          <div className="calendar-column">
            <div className="calendar-section">
              {/* Monatsnavigation */}
              <div className="calendar-header">
                <button onClick={prevMonth}>←</button>
                <div className="calendar-title">
                  {currentDate.toLocaleString("de-DE", {
                    month: "long",
                    year: "numeric",
                  })}
                </div>
                <button onClick={nextMonth}>→</button>
              </div>

              {/* Wochentage */}
              <div className="calendar-grid weekdays">
                {WEEKDAYS.map((wd) => (
                  <div key={wd} className="weekday-header">
                    {wd}
                  </div>
                ))}
              </div>

              {/* Tage */}
              <div className="calendar-grid days">
                {calendarDays.map((date, idx) => {
                  const isToday =
                    date.toDateString() === new Date().toDateString();
                  const inCurrentMonth = date.getMonth() === month;
                  const isSelected = selectedCalendarDate?.toDateString() === date.toDateString();

                  return (
                    <div
                      key={idx}
                      className={`calendar-cell ${
                        !inCurrentMonth ? "faded" : ""
                      } ${isToday ? "today" : ""} ${isSelected ? "selected" : ""}`}
                      onClick={() => {
                        setSelectedCalendarDate(date);
                        setShowPastEvents(false);
                      }}
                    >
                      <div className="date-header">{date.getDate()}</div>
                      <div className="event-list">
                        {eventsForDate(date).map((event) => {
                          const contact = contacts.find(c => c.id === event.contactId);
                          const cannotContact = contact?.canContact === false;
                          return (
                            <div
                              key={event.eventId}
                              className={`calendar-event ${cannotContact ? "cannot-contact" : ""}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedContactId(event.contactId);
                              }} // ✅ ID im Context speichern
                            >
                              {event.contactName}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Rechte Spalte: Sidebar oder ContactInfo */}
        {!selectedContactId ? (
          <div className="sidebar-section">
            <h3>{showPastEvents ? "Vergangene Events" : "Events vom " + selectedCalendarDate.toLocaleDateString("de-DE")}</h3>
            <button 
              className="toggle-past-events"
              onClick={() => setShowPastEvents(!showPastEvents)}
            >
              {showPastEvents ? "Hide Past Events" : "Show Past Events"}
            </button>
            <ul>
              {eventsToDisplay.length === 0 ? (
                <li className="no-events">Keine Events</li>
              ) : (
                eventsToDisplay.map((event) => (
                  <li 
                    key={event.eventId}
                    className="event-item"
                    onClick={() => setSelectedContactId(event.contactId)}
                  >
                    <span className="event-date">{event.day}</span>
                    <span className="event-contact">{event.contactName}</span>
                    {event.description && (
                      <span className="event-desc">{event.description}</span>
                    )}
                  </li>
                ))
              )}
            </ul>
          </div>
        ) : (
          <div className="split-right-panel">
            <div className="calendar-sidebar">
              <h3>{showPastEvents ? "Vergangene Events" : "Events vom " + selectedCalendarDate.toLocaleDateString("de-DE")}</h3>
              <button 
                className="toggle-past-events"
                onClick={() => setShowPastEvents(!showPastEvents)}
              >
                {showPastEvents ? "Hide Past Events" : "Show Past Events"}
              </button>
              <ul>
                {eventsToDisplay.length === 0 ? (
                  <li className="no-events">Keine Events</li>
                ) : (
                  eventsToDisplay.map((event) => (
                    <li 
                      key={event.eventId}
                      className="event-item"
                      onClick={() => setSelectedContactId(event.contactId)}
                    >
                      <span className="event-date">{event.day}</span>
                      <span className="event-contact">{event.contactName}</span>
                      {event.description && (
                        <span className="event-desc">{event.description}</span>
                      )}
                    </li>
                  ))
                )}
              </ul>
            </div>
            <ContactInfo contactId={selectedContactId} />
          </div>
        )}
      </div>
    </div>
  );
}
